import { Button } from "@msuite/ui/button";
import { DateInput } from "@msuite/ui/date-input";
import { useDebouncedValue, useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { useDataTableContext } from "@msuite/ui/table";
import { moment } from "@msuite/utils/date";
import { getUserAbsencesSchema } from "@msuite/utils/schemas";
import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useCitadel } from "@/hooks/use-citadel";
import { AddAttendanceModal } from "@/modals/users/add-attendance-modal";

/** Props Interface */
interface UserAttendancesMenuProps {
	onDatesChange: (startDate: string, endDate: string) => void;
}

export function UserAttendancesMenu({ onDatesChange }: UserAttendancesMenuProps) {
	/** Context */
	const { refetch } = useDataTableContext();

	/** Hooks */
	const { t } = useTranslation();
	const disclosure = useDisclosure();
	const { citadel } = useCitadel();
	const methods = useForm({
		resolver: resolver(getUserAbsencesSchema.pick({ startDate: true, endDate: true })),
		defaultValues: {
			startDate: moment().startOf("month").db(),
			endDate: moment().endOf("month").db(),
		},
	});

	/** State */
	const [startDate, endDate] = useWatch({
		control: methods.control,
		name: ["startDate", "endDate"],
	});
	const debouncedStartDate = useDebouncedValue(startDate, 500);
	const debouncedEndDate = useDebouncedValue(endDate, 500);

	/** Effects */
	useEffect(() => {
		onDatesChange(debouncedStartDate, debouncedEndDate);
	}, [debouncedStartDate, debouncedEndDate, onDatesChange]);

	/** Computed */
	// Picking the person is a personnel role's: a member's rule reaches their own sheet only, which
	// they add to from that sheet.
	const canAddForAnybody = citadel?.isUnrestrictedFor("create", "Attendance") === true;

	/** Render */
	return (
		<div className="h-stack **:text-xs!">
			<DateInput
				label={undefined}
				control={methods.control}
				name="startDate"
				prefix={t("dates.start_date")}
			/>
			<DateInput
				label={undefined}
				control={methods.control}
				name="endDate"
				prefix={t("dates.end_date")}
			/>
			{canAddForAnybody && (
				<Button
					variant="primary"
					onClick={disclosure.onOpen}
					icon={<Icon name="Plus" />}
				>
					{t("users.add_attendance")}
				</Button>
			)}
			{disclosure.isOpen && (
				<AddAttendanceModal
					isOpen={disclosure.isOpen}
					onClose={disclosure.onClose}
					onComplete={refetch}
				/>
			)}
		</div>
	);
}
